import { useEffect, useRef, useState } from "react";

/**
 * Internal state type for intersection observer
 * @property {boolean} isIntersecting - Whether the element is intersecting
 * @property {IntersectionObserverEntry} [entry] - The intersection observer entry
 */
type State = {
  /**
   * Whether the element is intersecting
   */
  isIntersecting: boolean;
  /**
   * The intersection observer entry
   */
  entry?: IntersectionObserverEntry;
};

/**
 * Options for the useIntersectionObserver hook
 * @property {Element | Document | null} [root] - The root element for intersection calculations
 * @property {string} [rootMargin] - Margin around the root element
 * @property {number | number[]} [threshold] - Threshold values for intersection detection
 * @property {boolean} [freezeOnceVisible] - Whether to freeze once the element becomes visible
 * @property {(isIntersecting: boolean, entry: IntersectionObserverEntry) => void} [onChange] - Callback when intersection changes
 * @property {boolean} [initialIsIntersecting] - Initial intersection state
 */
export type UseIntersectionObserverOptions = {
  /**
   * The root element for intersection calculations
   */
  root?: Element | Document | null;
  /**
   * Margin around the root element
   */
  rootMargin?: string;
  /**
   * Threshold values for intersection detection
   */
  threshold?: number | number[];
  /**
   * Whether to freeze once the element becomes visible
   */
  freezeOnceVisible?: boolean;
  /**
   * Callback when intersection changes
   */
  onChange?: (
    isIntersecting: boolean,
    entry: IntersectionObserverEntry,
  ) => void;
  /**
   * Initial intersection state
   */
  initialIsIntersecting?: boolean;
};

/**
 * Return type for the useIntersectionObserver hook
 * @property {(node?: Element | null) => void} ref - Function to set the element to observe
 * @property {boolean} isIntersecting - Whether the element is intersecting
 * @property {IntersectionObserverEntry} [entry] - The intersection observer entry
 */
export type IntersectionReturn = [
  (node?: Element | null) => void,
  boolean,
  IntersectionObserverEntry | undefined,
] & {
  ref: (node?: Element | null) => void;
  isIntersecting: boolean;
  entry?: IntersectionObserverEntry;
};

/**
 * Custom hook for observing element intersection with viewport or root element.
 *
 * Features:
 * - Observes element intersection with viewport or custom root element
 * - Supports configurable threshold values and root margins
 * - Provides intersection state and detailed entry information
 * - Supports freezing observation once element becomes visible
 * - Handles browser compatibility gracefully
 * - Provides both array and object destructuring support
 * - Automatically cleans up observer on unmount
 * - Supports onChange callback for custom intersection handling
 *
 * @param options - Configuration options for the intersection observer
 * @returns Array containing ref function, intersection state, and entry, with additional object properties
 */
export function useIntersectionObserver({
  threshold = 0,
  root = null,
  rootMargin = "0%",
  freezeOnceVisible = false,
  initialIsIntersecting = false,
  onChange,
}: UseIntersectionObserverOptions = {}): IntersectionReturn {
  const [ref, setRef] = useState<Element | null>(null);

  const [state, setState] = useState<State>(() => ({
    isIntersecting: initialIsIntersecting,
    entry: undefined,
  }));

  const callbackRef = useRef<UseIntersectionObserverOptions["onChange"]>();

  callbackRef.current = onChange;

  const frozen = state.entry?.isIntersecting && freezeOnceVisible;

  useEffect(() => {
    // Ensure we have a ref to observe
    if (!ref) return;

    // Ensure the browser supports the Intersection Observer API
    if (!("IntersectionObserver" in window)) return;

    // Skip if frozen
    if (frozen) return;

    let unobserve: (() => void) | undefined;

    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]): void => {
        const thresholds = Array.isArray(observer.thresholds)
          ? observer.thresholds
          : [observer.thresholds];

        entries.forEach((entry) => {
          const isIntersecting =
            entry.isIntersecting &&
            thresholds.some(
              (threshold) => entry.intersectionRatio >= threshold,
            );

          setState({ isIntersecting, entry });

          if (callbackRef.current) {
            callbackRef.current(isIntersecting, entry);
          }

          if (isIntersecting && freezeOnceVisible && unobserve) {
            unobserve();
            unobserve = undefined;
          }
        });
      },
      { threshold, root, rootMargin },
    );

    observer.observe(ref);

    return () => {
      observer.disconnect();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ref,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(threshold),
    root,
    rootMargin,
    frozen,
    freezeOnceVisible,
  ]);

  // ensures that if the observed element changes, the intersection observer is reinitialized
  const prevRef = useRef<Element | null>(null);

  useEffect(() => {
    if (
      !ref &&
      state.entry?.target &&
      !freezeOnceVisible &&
      !frozen &&
      prevRef.current !== state.entry.target
    ) {
      prevRef.current = state.entry.target;
      setState({ isIntersecting: initialIsIntersecting, entry: undefined });
    }
  }, [ref, state.entry, freezeOnceVisible, frozen, initialIsIntersecting]);

  const result = [
    setRef,
    !!state.isIntersecting,
    state.entry,
  ] as IntersectionReturn;

  // Support object destructuring, by adding the specific values.
  result.ref = result[0];
  result.isIntersecting = result[1];
  result.entry = result[2];

  return result;
}
